import { useState, useEffect } from "react";
import {
  Box,
  Button,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Select,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import {
  Wrapper,
  Calender,
} from "../../components";
import { CustomerAnalyticsDashboardContent } from "../../components/dashboard";
import {
  Logs,
  downloadLogs,
  platformDashboardSummary,
  topCustomersdata,
} from "../../services";

import SearchIcon from "@mui/icons-material/Search";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DownloadIcon from "@mui/icons-material/Download";

dayjs.extend(relativeTime);

export function CustomerAnalyticsDashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const jwtToken = user?.jwt;
  const CONTROL_HEIGHT = 38;

  /* ================= STATE ================= */
  const [dashboardSummaryData, setDashboardSummaryData] = useState(null);

  /* Filters */
  const [status, setStatus] = useState("All Status");
  const [search, setSearch] = useState("");

  /* Status Menu */
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);

  /* ===== Date Filter ===== */
  const [days, setDays] = useState("7");
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarKey, setCalendarKey] = useState(0);

  const currentDate = dayjs();
  const sevenDaysBack = currentDate.subtract(7, "day");

  const defaultDateRange = [
    sevenDaysBack.startOf("day"),
    currentDate.endOf("day"),
  ];

  const [dateRangeValue, setDateRangeValue] = useState(defaultDateRange);
  const [dashboardDates, setDashboardDates] = useState({
    startDate: defaultDateRange[0].format("YYYY-MM-DD"),
    endDate: defaultDateRange[1].format("YYYY-MM-DD"),
  });

  /* ================= API CALLS ================= */

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const [customers, setCustomers] = useState([]);
  const [totalCustomersCount, setTotalCustomersCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  useEffect(() => {
    if (!jwtToken || !dashboardDates) return;
  
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

    const summaryRes = await platformDashboardSummary(jwtToken, dashboardDates);
    if (summaryRes?.status === 200) {
      setDashboardSummaryData(summaryRes.data);
    }

    } catch (error) {
      console.error("Dashboard API error:", error);
    } finally {
      setLoading(false);
    }
  };
  fetchDashboardData();

  }, [jwtToken, dashboardDates]);
  
  useEffect(() => {
    if (!jwtToken) return;
  
    const fetchTopCustomers = async () => {
      try {
        setTableLoading(true);
        const apiStatus = status === "All Status" ? undefined : status;
        const res = await topCustomersdata(jwtToken, page, rowsPerPage, apiStatus, search, dashboardDates);
        if (res?.status === 200) {
          setCustomers(res.data?.data || []);
          setTotalCustomersCount(
            res.data?.pagination?.totalCompanies || 0
          );
        }
      } catch (error) {
        console.error("Top customers API error:", error);
      } finally {
        setTableLoading(false);
      }
    };
    fetchTopCustomers();
  }, [jwtToken, status, search, page, rowsPerPage, dashboardDates]);
  
  useEffect(() => {
    setPage(1);
  }, [status, search, rowsPerPage, dashboardDates]);
  
  /* ================= DATE HANDLERS ================= */
  const handleDaysChange = (value) => {
    setDays(value);

    if (value === "custom") {
      setCalendarKey((prev) => prev + 1);
      setCalendarOpen(true);
      return;
    }

    setCalendarOpen(false);

    const endDate = dayjs();
    const startDate = endDate.subtract(Number(value), "day");
    const newRange = [startDate.startOf("day"), endDate.endOf("day")];

    setDateRangeValue(newRange);
    setDashboardDates({
      startDate: newRange[0].format("YYYY-MM-DD"),
      endDate: newRange[1].format("YYYY-MM-DD"),
    });
  };

  const handleChangeCalender = (dates) => {
    if (!dates) return;

    setDateRangeValue(dates);
    setDashboardDates({
      startDate: dates[0].format("YYYY-MM-DD"),
      endDate: dates[1].format("YYYY-MM-DD"),
    });
  };

  /* ================= EXPORT ================= */
  const handleExport = async () => {
    try {
      const response = await downloadLogs(null, dashboardDates);
      if (response?.data) {
        const url = URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = "customers.xlsx";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error(error);
    }
  };

  /* ================= UI ================= */
  return (
    <Wrapper>
      <Stack sx={{ pt: 2, mt: 7 }} spacing={2} className="wrapper">
        <Typography color="#424242" fontWeight="regular" sx={{ opacity: 0.8 }}>
          View detailed analytics for all onboarded customers
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          {/* Search */}
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            size="small"
            sx={{
              width: 280,
              "& .MuiOutlinedInput-root": {
                height: CONTROL_HEIGHT,
                borderRadius: 3,
                backgroundColor: "#fff",

                "&.Mui-focused": {
                  backgroundColor: "#fff",
                },

                "& input:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 1000px #fff inset",
                  WebkitTextFillColor: "#000",
                },
              },

              "& input::selection": {
                backgroundColor: "#e2e8f0",
                color: "#000",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Status Filter */}
          <Button
            variant="outlined"
            startIcon={<FilterAltOutlinedIcon sx={{ fontSize: 18 }} />}
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
            sx={{
              height: CONTROL_HEIGHT,
              minHeight: CONTROL_HEIGHT,
              textTransform: "none",
              borderRadius: 3,
              px: 2,
              fontSize: "14px",
              color: "#0f172a",
              borderColor: "#e2e8f0",
              backgroundColor: "#fff",
            }}
            onClick={(e) => setStatusAnchorEl(e.currentTarget)}
          >
            {status}
          </Button>

          {/* Date Filter */}
          <Box sx={{ ml: "auto", display: "flex", gap: 1,
              "@media (max-width: 767px)": {
                ml: "inherit",
              }
           }}>
            <Select
              size="small"
              value={days}
              onChange={(e) => handleDaysChange(e.target.value)}
              sx={{
                height: CONTROL_HEIGHT,
                borderRadius: 3,
                bgcolor: "#fff",
                fontSize: "14px",
              }}
            >
              <MenuItem value="7">Last 7 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="90">Last 90 days</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>

            {calendarOpen && (
              <Box key={calendarKey}>
                <Calender
                  handleChange={handleChangeCalender}
                  defaultValue={defaultDateRange}
                  value={dateRangeValue}
                  maxDate={new Date()}
                />
              </Box>
            )}
          </Box>

          {/* Export */}
          {/* <Button
            variant="outlined"
            startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
            sx={{
              height: CONTROL_HEIGHT,
              minHeight: CONTROL_HEIGHT,
              textTransform: "none",
              borderRadius: "10px",
              px: 2,
              fontSize: "14px",
              color: "#0f172a",
              borderColor: "#e2e8f0",
              backgroundColor: "#fff",
              ml: "auto",
            }}
            onClick={handleExport}
          >
            Export
          </Button> */}

          {/* Menu */}
          <Menu
            anchorEl={statusAnchorEl}
            open={Boolean(statusAnchorEl)}
            onClose={() => setStatusAnchorEl(null)}
            PaperProps={{
              sx: {
                borderRadius: "10px",
                mt: 1,
                minWidth: 160,
              },
            }}
          >
            {["All Status", "Active", "Inactive", "Churned"].map((item) => (
              <MenuItem
                key={item}
                onClick={() => {
                  setStatus(item);
                  setStatusAnchorEl(null);
                }}
              >
                {item}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        <CustomerAnalyticsDashboardContent
          loading={loading}
          tableLoading={tableLoading}
          totalCustomers={dashboardSummaryData?.totalCustomers || 0}
          activeCustomers={dashboardSummaryData?.activeCustomers || 0}
          totalApiCalls={dashboardSummaryData?.totalApiCalls || 0}
          totalCreditConsumed={dashboardSummaryData?.totalCreditConsumed || 0}
          customers={customers}
          totalCustomersCount={totalCustomersCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
        />
      </Stack>
    </Wrapper>
  );
} 